import React, { useCallback, useEffect, useMemo } from "react";
import { connect } from "react-redux";

import BoundaryType from "@mapmycustomers/shared/enum/BoundaryType";
import MapTool from "@mapmycustomers/shared/enum/map/MapTool";
import { Territory } from "@mapmycustomers/shared/types/entity";

import TerritoryFeature from "@app/scene/map/components/features/TerritoryFeature";
import { initializeTerritories } from "@app/scene/map/store/layers/boundaries/actions";
import {
  areBoundariesVisible,
  getBoundaryType,
  getHighlightedTerritoryId,
} from "@app/scene/map/store/layers/boundaries/selectors";
import { getMapViewTool, getTerritory } from "@app/scene/map/store/selectors";
import { enterTerritoryMode } from "@app/scene/map/store/territoryMode/actions";
import { doesOrganizationSupportTerritories } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { getTerritories } from "@app/store/territories";

interface Props {
  highlightedTerritoryId?: number | undefined;
  initialize: typeof initializeTerritories.request;
  onEnterTerritoryMode: typeof enterTerritoryMode;
  selectedTerritory?: Territory;
  selectedTool?: MapTool;
  territories: Territory[];
  visible: boolean;
}

const TerritoriesLayer: React.FC<Props> = ({
  highlightedTerritoryId,
  initialize,
  onEnterTerritoryMode,
  selectedTerritory,
  selectedTool,
  territories,
  visible,
}) => {
  useEffect(() => {
    if (visible) {
      // TODO: consider only calling initialize once since we're fetching all territories anyway
      initialize();
    }
  }, [initialize, visible]);

  const visibleTerritories = useMemo(() => {
    return territories.filter((territory) => !territory.territoryDetail.hidden);
  }, [territories]);

  const handleSelectTerritory = useCallback(
    (territory: Territory) => {
      onEnterTerritoryMode?.({ territory, territoryId: territory.id });
    },
    [onEnterTerritoryMode]
  );

  return visible ? (
    <TerritoryFeature
      disabled={selectedTool === MapTool.LASSO}
      highlightedTerritoryId={highlightedTerritoryId}
      onOpen={handleSelectTerritory}
      selectedTerritoryId={selectedTerritory?.id}
      territories={visibleTerritories}
    />
  ) : null;
};

const mapStateToProps = (state: RootState) => {
  const enabled: boolean = doesOrganizationSupportTerritories(state);
  const visible =
    areBoundariesVisible(state) && enabled && getBoundaryType(state) === BoundaryType.TERRITORY;
  return {
    highlightedTerritoryId: getHighlightedTerritoryId(state),
    selectedTerritory: getTerritory(state),
    selectedTool: getMapViewTool(state),
    territories: getTerritories(state),
    visible,
  };
};

const mapDispatchToProps = {
  initialize: initializeTerritories.request,
  onEnterTerritoryMode: enterTerritoryMode,
};

export default connect(mapStateToProps, mapDispatchToProps)(TerritoriesLayer);
